import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Zap,
  ArrowLeft,
  MessageSquare,
  Heart,
  User,
  Plus,
  Send,
  Tag,
  ChevronDown,
  ChevronUp,
  Trash2,
  Filter,
  Search,
  MapPin,
  Briefcase,
  Building2
} from "lucide-react";

const INDUSTRIES = [
  "Technology", "Healthcare", "Finance", "Education", "Marketing",
  "Sales", "Engineering", "Design", "Legal", "Manufacturing", "Remote Work"
];

const Community = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [newPost, setNewPost] = useState({ title: "", content: "", tags: [] });
  const [newTag, setNewTag] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [expandedPost, setExpandedPost] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    tag: "",
    industry: "",
    location: "",
    job_type: ""
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async (filterParams = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterParams.tag) params.append("tag", filterParams.tag);
      if (filterParams.industry) params.append("industry", filterParams.industry);
      if (filterParams.location) params.append("location", filterParams.location);
      if (filterParams.job_type) params.append("job_type", filterParams.job_type);

      const response = await fetch(`${API}/community/posts?${params.toString()}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error("Failed to fetch posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    fetchPosts(filters);
    setShowFilters(false);
  };

  const clearFilters = () => {
    const emptyFilters = { tag: "", industry: "", location: "", job_type: "" };
    setFilters(emptyFilters);
    fetchPosts(emptyFilters);
  };

  const handleCreatePost = async () => {
    if (!newPost.title || !newPost.content) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/community/posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newPost),
      });

      if (response.ok) {
        toast.success("Post created!");
        setShowCreatePost(false);
        setNewPost({ title: "", content: "", tags: [] });
        fetchPosts();
      } else {
        toast.error("Failed to create post");
      }
    } catch (error) {
      toast.error("Failed to create post");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (postId) => {
    if (!user) {
      toast.error("Please login to like posts");
      return;
    }

    try {
      const response = await fetch(`${API}/community/posts/${postId}/like`, {
        method: "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setPosts(prev => prev.map(p => 
          p.post_id === postId 
            ? { ...p, likes: data.likes, user_has_liked: data.liked } 
            : p
        ));
      }
    } catch (error) {
      console.error("Failed to like:", error);
    }
  };

  const handleComment = async (postId) => {
    if (!user) {
      toast.error("Please login to comment");
      return;
    }

    if (!newComment.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    setSubmittingComment(true);
    try {
      const response = await fetch(`${API}/community/posts/${postId}/comment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: newComment }),
      });

      if (response.ok) {
        const comment = await response.json();
        setPosts(prev => prev.map(p => 
          p.post_id === postId 
            ? { ...p, comments: [...(p.comments || []), comment] } 
            : p
        ));
        setNewComment("");
        toast.success("Comment added!");
      } else {
        toast.error("Failed to add comment");
      }
    } catch (error) {
      toast.error("Failed to add comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    try {
      const response = await fetch(`${API}/community/posts/${postId}/comment/${commentId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        setPosts(prev => prev.map(p => 
          p.post_id === postId 
            ? { ...p, comments: p.comments.filter(c => c.comment_id !== commentId) } 
            : p
        ));
        toast.success("Comment deleted");
      }
    } catch (error) {
      toast.error("Failed to delete comment");
    }
  };

  const addTag = () => {
    if (newTag.trim() && !newPost.tags.includes(newTag.trim())) {
      setNewPost(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
      setNewTag("");
    }
  };

  const toggleComments = (postId) => {
    setExpandedPost(expandedPost === postId ? null : postId);
    setNewComment("");
  };

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-4 sm:p-6 pt-20 lg:pt-8">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
                <span className="text-crimson">COMMUNITY</span>
              </h1>
              <p className="text-gray-400 text-sm sm:text-base">
                Share insights, ask questions, and connect
              </p>
            </div>
            {user && (
              <Button
                onClick={() => setShowCreatePost(true)}
                className="bg-crimson hover:bg-crimson-hover text-white font-bold uppercase tracking-wider text-sm sm:text-base w-full sm:w-auto"
                data-testid="create-post-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> New Post
              </Button>
            )}
          </div>

          {/* Filters Section */}
          <motion.div 
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4 flex-wrap flex-1">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    value={filters.tag}
                    onChange={(e) => setFilters(prev => ({ ...prev, tag: e.target.value }))}
                    onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                    placeholder="Search by keyword or tag..."
                    className="bg-obsidian-light border-[#333] text-white placeholder:text-gray-600 pl-10"
                    data-testid="search-community"
                  />
                </div>
                <Button
                  onClick={() => setShowFilters(!showFilters)}
                  variant="outline"
                  className={`border-[#333] text-white ${showFilters ? "border-crimson" : ""}`}
                >
                  <Filter className="w-4 h-4 mr-2" /> Filters
                </Button>
                <Button
                  onClick={applyFilters}
                  className="bg-crimson hover:bg-crimson-hover text-white"
                >
                  Search
                </Button>
              </div>
            </div>

            {/* Expanded Filters */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  className="mt-4 p-4 bg-obsidian-light border border-[#222]"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block flex items-center gap-2">
                        <Building2 className="w-3 h-3" /> Industry
                      </label>
                      <select
                        value={filters.industry}
                        onChange={(e) => setFilters(prev => ({ ...prev, industry: e.target.value }))}
                        className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white"
                      >
                        <option value="">All Industries</option>
                        {INDUSTRIES.map(ind => (
                          <option key={ind} value={ind}>{ind}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block flex items-center gap-2">
                        <MapPin className="w-3 h-3" /> Location
                      </label>
                      <Input
                        value={filters.location}
                        onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                        placeholder="e.g., Remote, New York"
                        className="bg-obsidian border-[#333] text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block flex items-center gap-2">
                        <Briefcase className="w-3 h-3" /> Job Type
                      </label>
                      <Input
                        value={filters.job_type}
                        onChange={(e) => setFilters(prev => ({ ...prev, job_type: e.target.value }))}
                        placeholder="e.g., Developer, Marketing"
                        className="bg-obsidian border-[#333] text-white"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 mt-4">
                    <Button
                      onClick={clearFilters}
                      variant="ghost"
                      className="text-gray-400 hover:text-white"
                    >
                      Clear All
                    </Button>
                    <Button
                      onClick={applyFilters}
                      className="bg-crimson hover:bg-crimson-hover text-white uppercase tracking-wider"
                    >
                      Apply Filters
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Create Post Modal */}
          <AnimatePresence>
            {showCreatePost && (
              <motion.div 
                className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div 
                  className="bg-obsidian-light border border-[#222] p-6 w-full max-w-lg"
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.9 }}
                >
                  <h3 className="font-heading text-2xl font-bold mb-6">CREATE POST</h3>
                  
                  <div className="space-y-4">
                    <Input
                      value={newPost.title}
                      onChange={(e) => setNewPost(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Post title"
                      className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                      data-testid="post-title-input"
                    />
                    
                    <Textarea
                      value={newPost.content}
                      onChange={(e) => setNewPost(prev => ({ ...prev, content: e.target.value }))}
                      placeholder="Share your thoughts, tips, or questions..."
                      className="bg-obsidian border-[#333] text-white placeholder:text-gray-600 min-h-[150px]"
                      data-testid="post-content-input"
                    />

                    <div className="flex gap-2">
                      <Input
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                        placeholder="Add tags"
                        className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                      />
                      <Button onClick={addTag} variant="outline" className="border-[#333]">
                        <Tag className="w-4 h-4" />
                      </Button>
                    </div>

                    {newPost.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {newPost.tags.map((tag, idx) => (
                          <span key={idx} className="px-2 py-1 bg-crimson/10 border border-crimson/30 text-crimson text-sm">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button
                      onClick={() => setShowCreatePost(false)}
                      variant="outline"
                      className="flex-1 border-[#333] text-white"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCreatePost}
                      disabled={submitting}
                      className="flex-1 bg-crimson hover:bg-crimson-hover text-white"
                      data-testid="submit-post-btn"
                    >
                      {submitting ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" /> Post
                        </>
                      )}
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Posts */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
            </div>
          ) : posts.length > 0 ? (
            <div className="space-y-6">
              {posts.map((post, index) => (
                <motion.div
                  key={post.post_id}
                  className="bg-obsidian-light border border-[#222] p-6 hover:border-crimson/50 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  data-testid={`post-${post.post_id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-crimson/20 flex items-center justify-center flex-shrink-0">
                      {post.author_picture ? (
                        <img src={post.author_picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-5 h-5 text-crimson" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-bold">{post.author_name}</span>
                        <span className="text-gray-500 text-sm">
                          {new Date(post.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <h3 className="font-heading text-xl font-bold mb-2">{post.title}</h3>
                      <p className="text-gray-300 mb-4 whitespace-pre-wrap">{post.content}</p>
                      
                      {post.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {post.tags.map((tag, idx) => (
                            <span key={idx} className="text-crimson text-sm">#{tag}</span>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => handleLike(post.post_id)}
                          className={`flex items-center gap-1 transition-colors ${
                            post.user_has_liked 
                              ? "text-crimson" 
                              : "text-gray-400 hover:text-crimson"
                          }`}
                          data-testid={`like-btn-${post.post_id}`}
                        >
                          <Heart className={`w-4 h-4 ${post.user_has_liked ? "fill-crimson" : ""}`} />
                          <span>{post.likes}</span>
                        </button>
                        <button 
                          onClick={() => toggleComments(post.post_id)}
                          className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors"
                          data-testid={`comments-btn-${post.post_id}`}
                        >
                          <MessageSquare className="w-4 h-4" />
                          <span>{post.comments?.length || 0}</span>
                          {expandedPost === post.post_id ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                      </div>

                      {/* Comments Section */}
                      <AnimatePresence>
                        {expandedPost === post.post_id && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="mt-4 pt-4 border-t border-[#222] overflow-hidden"
                          >
                            {/* Comment Input */}
                            {user && (
                              <div className="flex gap-2 mb-4">
                                <Input
                                  value={newComment}
                                  onChange={(e) => setNewComment(e.target.value)}
                                  onKeyDown={(e) => e.key === "Enter" && handleComment(post.post_id)}
                                  placeholder="Write a comment..."
                                  className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                                  data-testid={`comment-input-${post.post_id}`}
                                />
                                <Button
                                  onClick={() => handleComment(post.post_id)}
                                  disabled={submittingComment || !newComment.trim()}
                                  className="bg-crimson hover:bg-crimson-hover text-white"
                                  data-testid={`submit-comment-${post.post_id}`}
                                >
                                  {submittingComment ? (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <Send className="w-4 h-4" />
                                  )}
                                </Button>
                              </div>
                            )}

                            {/* Comments List */}
                            <div className="space-y-3">
                              {post.comments && post.comments.length > 0 ? (
                                post.comments.map((comment) => (
                                  <div 
                                    key={comment.comment_id} 
                                    className="flex items-start gap-3 p-3 bg-obsidian"
                                    data-testid={`comment-${comment.comment_id}`}
                                  >
                                    <div className="w-8 h-8 bg-crimson/20 flex items-center justify-center flex-shrink-0">
                                      {comment.author_picture ? (
                                        <img src={comment.author_picture} alt="" className="w-full h-full object-cover" />
                                      ) : (
                                        <User className="w-4 h-4 text-crimson" />
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-bold text-sm">{comment.author_name}</span>
                                        <span className="text-gray-500 text-xs">
                                          {new Date(comment.created_at).toLocaleDateString()}
                                        </span>
                                      </div>
                                      <p className="text-gray-300 text-sm mt-1">{comment.content}</p>
                                    </div>
                                    {user?.user_id === comment.author_id && (
                                      <button
                                        onClick={() => handleDeleteComment(post.post_id, comment.comment_id)}
                                        className="text-gray-500 hover:text-crimson"
                                        data-testid={`delete-comment-${comment.comment_id}`}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                ))
                              ) : (
                                <p className="text-gray-500 text-sm text-center py-4">
                                  No comments yet. Be the first to comment!
                                </p>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="font-heading text-2xl font-bold mb-2">No Posts Yet</h3>
              <p className="text-gray-400 mb-6">Be the first to share something with the community</p>
              {user && (
                <Button
                  onClick={() => setShowCreatePost(true)}
                  className="bg-crimson hover:bg-crimson-hover text-white uppercase tracking-wider"
                >
                  Create First Post
                </Button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Community;
